<!-- frontend/src/App.vue -->
<template>
  <div class="h-screen flex flex-col bg-gray-100">
    <AppHeader
      v-if="authStore.isAuthenticated && !isLoginPage"
      :connected="wsConnected"
      :ship-count="shipsStore.ships.length"
      :filtered-ship-count="visibleShipCount"
    />
    
    <div class="flex-1 flex overflow-hidden">
      <!-- Dynamic Sidebar -->
      <div 
        v-if="authStore.isAuthenticated && !isLoginPage"
        class="relative transition-all duration-300 ease-in-out"
        :class="sidebarWidthClass"
      >
        <div 
          v-show="uiStore.sidebarOpen"
          class="h-full border-r bg-white overflow-hidden"
        >
          <ShipSidebar
            v-if="uiStore.activePage === 'map'"
            :ships="shipsForSidebar"
            :selected-ship="selectedShipForSidebar"
            :loading="shipsStore.loading"
            :sources="authStore.sources.map(s => s.name)"
            @select-ship="handleShipSelectFromSidebar"
            @refresh="refreshShips"
            @filtered-count-change="handleSidebarFilteredCount"
            @source-filter-change="handleSourceFilterChange"
          />
          
          <CameraSidebar
            v-else-if="uiStore.activePage === 'cameras'"
            :cameras="camerasForSidebar"
            :loading="camerasStore.loading"
            :selected-camera="selectedCameraForSidebar"
            :selected-source="uiStore.selectedCameraSource"
            @select-camera="handleCameraSelectFromSidebar"
            @select-source="handleCameraSourceSelect"
            @show-stream="showFloatingStream"
            @close="uiStore.setSidebarOpen(false)"
          />
          
          <div v-else-if="uiStore.activePage === 'stats'" class="bg-white h-full flex items-center justify-center">
            <div class="text-center text-gray-400">
              <i class="fas fa-chart-line text-4xl mb-2 opacity-30"></i>
              <p class="text-sm">Statistics Panel</p>
              <p class="text-xs">Coming soon...</p>
            </div>
          </div>
        </div>
        
        <button
          @click="uiStore.toggleSidebar"
          class="absolute -right-2.5 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-white border rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors z-20"
        >
          <i 
            class="fas fa-chevron-left text-gray-500 text-[8px] transition-transform duration-300"
            :class="{ 'rotate-180': !uiStore.sidebarOpen }"
          ></i>
        </button>
      </div>
      
      <div class="flex-1 relative">
        <router-view
          :ships="shipsStore.ships"
          :filtered-ships="shipsStore.filteredShips"
          :selected-ship="selectedShipForMap"
          :cameras="camerasStore.cameras"
          :ws-connected="wsConnected"
          :selected-ship-from-sidebar="selectedShipForMap"
          :admin-source-filter="uiStore.adminSourceFilter"
          :selected-source="uiStore.selectedCameraSource"
          :show-ships-prop="shipsVisible"
          :show-cameras-prop="camerasVisible"
          @ship-select="handleShipSelect"
          @camera-select="handleCameraSelect"
          @map-ready="handleMapReady"
          @track-ship="openPlaybackForShip"
          @filtered-ships-change="handleMapFilteredShipsChange"
          @refresh-cameras="refreshCameras"
          @source-filter-change="handleSourceFilterChange"
          @select-source="handleCameraSourceSelect"
          @toggle-layer="handleToggleLayer"
        />
      </div>
      
      <transition name="slide-right">
        <div
          v-if="uiStore.cameraPanelOpen && authStore.isAuthenticated && !isLoginPage"
          class="w-80 border-l bg-white overflow-hidden flex-shrink-0"
        >
          <CameraPanel 
            :initial-cameras="camerasStore.filteredCameras"
            @close="uiStore.toggleCameraPanel()"
            @select-camera="handleCameraSelect"
          />
        </div>
      </transition>
    </div>
    
    <TimelinePlayer 
      v-if="playbackMode"
      :enabled="playbackMode"
      :initial-ship="trackedShip"
      :initial-date="playbackDate"
      @close="closePlayback"
      @time-change="handleTimeChange"
      @snapshot-load="handleSnapshotLoad"
      @center-ship="centerOnShip"
    />
    
    <FloatingVideoStream
      ref="floatingStreamRef"
      :camera="floatingStreamCamera"
      :auto-show="false"
      :auto-rotate="true"
      @close="handleStreamClose"
      @camera-change="(camera) => {}"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { useUIStore } from './stores/ui'
import { useShipsStore } from './stores/ships'
import { useCamerasStore } from './stores/cameras'
import { useFilterStore } from './stores/filter'
import { useGeofenceStore } from './stores/geofenceStore'
import AppHeader from './components/Layout/AppHeader.vue'
import ShipSidebar from './components/UI/ShipSidebar.vue'
import CameraSidebar from './components/UI/CameraSidebar.vue'
import CameraPanel from './components/UI/CameraPanel.vue'
import TimelinePlayer from './components/UI/TimelinePlayer.vue'
import FloatingVideoStream from './components/Map/FloatingVideoStream.vue'

const route = useRoute()
const authStore = useAuthStore()
const uiStore = useUIStore()
const shipsStore = useShipsStore()
const camerasStore = useCamerasStore()
const filterStore = useFilterStore()
const geofenceStore = useGeofenceStore()

const isLoginPage = computed(() => route.path === '/login')
const sidebarWidthClass = computed(() => uiStore.sidebarOpen ? 'w-80' : 'w-0')
const wsConnected = ref(false)
const selectedShipForMap = ref(null)
const selectedShipForSidebar = ref(null)
const selectedCameraForSidebar = ref(null)
const visibleShipCount = ref(0)
const sidebarFilteredCount = ref(0)
const playbackMode = ref(false)
const trackedShip = ref(null)
const playbackDate = ref(null)
const floatingStreamCamera = ref(null)
const floatingStreamRef = ref(null)

let initialChunksAccumulator = []
let isInitializingAfterLogin = false

// Performance optimization: Batch WebSocket updates
let pendingBatchUpdates = []
let batchTimer = null
const BATCH_DELAY_MS = 50
let heartbeatInterval = null
let ws = null
let reconnectAttempts = 0
let maxReconnectAttempts = 5
let isConnecting = false
let autoStreamInterval = null
let currentCameraSource = 'all'

const shipsVisible = computed({
  get: () => uiStore.showShipsLayer,
  set: (val) => uiStore.setShowShipsLayer(val)
})

const camerasVisible = computed({
  get: () => uiStore.showCamerasLayer,
  set: (val) => uiStore.setShowCamerasLayer(val)
})

const shipsForSidebar = computed(() => shipsStore.ships)
const camerasForSidebar = computed(() => camerasStore.cameras)

// Queue batch update from WebSocket
function queueBatchUpdate(update) {
  if (!update || !update.mmsi) return
  
  pendingBatchUpdates.push(update)
  
  if (batchTimer) clearTimeout(batchTimer)
  batchTimer = setTimeout(() => {
    if (pendingBatchUpdates.length > 0) {
      shipsStore.applyBatchUpdate(pendingBatchUpdates)
      pendingBatchUpdates = []
    }
    batchTimer = null
  }, BATCH_DELAY_MS)
}

async function reinitializeAfterLogin() {
  if (isInitializingAfterLogin) return
  isInitializingAfterLogin = true
  
  console.log('🔄 Reinitializing data after login...')
  
  try {
    shipsStore.cleanup?.()
    shipsStore.ships = []
    camerasStore.cameras = []
    geofenceStore.geofences = []
    geofenceStore.activeShips = []
    geofenceStore.history = []
    
    await Promise.all([
      shipsStore.fetchShips(),
      authStore.fetchSources(),
      camerasStore.fetchCameras(),
      geofenceStore.refreshAll()
    ])
    
    if (ws) {
      try { ws.close() } catch(e) {}
      ws = null
    }
    wsConnected.value = false
    connectWebSocket()
    
    selectedShipForMap.value = null
    selectedShipForSidebar.value = null
    currentCameraSource = authStore.userSource !== 'all' ? authStore.userSource : 'all'
    
    console.log('✅ Reinitialization complete')
  } catch (error) {
    console.error('❌ Error during reinitialization:', error)
  } finally {
    isInitializingAfterLogin = false
  }
}

// App.vue - UPDATE function connectWebSocket()

function connectWebSocket() {
  if (isConnecting) return
  if (ws?.readyState === WebSocket.OPEN) return
  if (ws?.readyState === WebSocket.CONNECTING) return
  
  isConnecting = true
  
  const source = authStore.userSource
  const token = authStore.token
  const wsUrl = `/ws?source=${encodeURIComponent(source)}&token=${encodeURIComponent(token)}`
  
  try {
    ws = new WebSocket(wsUrl)
    
    ws.onopen = () => {
      wsConnected.value = true
      reconnectAttempts = 0
      isConnecting = false
      
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
        heartbeatInterval = null
      }
      
      heartbeatInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }))
        }
      }, 60000)
    }
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        
        // HANDLE AUTH ERROR DARI WEBSOCKET
        if (data.type === 'error' && (data.code === 401 || data.message === 'Unauthorized')) {
          console.log('WebSocket auth error, logging out...')
          authStore.logout()
          router.push('/login')
          if (window.showNotification) {
            window.showNotification('Session expired. Please login again.', 'error')
          }
          return
        }
        
        if (data.type === 'pong') return
        
        if (data.type === 'batch') {
          if (data.updates && data.updates.length > 0) {
            shipsStore.applyBatchUpdate(data.updates)
          }
        }
        else if (data.type === 'initial') {
          if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            shipsStore.setInitialShips(data.data)
          }
        }
        else if (data.type === 'initial_chunk') {
          if (data.data && Array.isArray(data.data)) {
            initialChunksAccumulator.push(...data.data)
          }
        }
        else if (data.type === 'initial_complete') {
          if (initialChunksAccumulator.length > 0) {
            shipsStore.setInitialShips(initialChunksAccumulator)
            initialChunksAccumulator = []
          }
        }
        else if (data.type === 'navigation_update') {
          if (data.data && data.mmsi) {
            shipsStore.updateShipNavigation(data.mmsi, data.data)
          }
        }
        else if (data.type === 'update') {
          if (data.data) {
            queueBatchUpdate(data.data)
          }
        }
        else if (data.type === 'geofence_alert') {
          geofenceStore.addAlert(data)
          if (Notification.permission === 'granted') {
            new Notification('Geofence Alert', {
              body: `🚢 ${data.ship_name || data.mmsi} ${data.type === 'geofence_entry' ? 'entered' : 'exited'} ${data.geofence_name}`,
              icon: '/vessel-icon.png'
            })
          }
        }
      } catch (error) {
        // Silent fail
      }
    }
    
    ws.onclose = (event) => {
      wsConnected.value = false
      isConnecting = false
      
      // Cek apakah ditutup karena auth error
      if (event.code === 4001 || event.code === 1008) {
        console.log('WebSocket closed due to auth error (code:', event.code, ')')
        authStore.logout()
        router.push('/login')
        return
      }
      
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
        heartbeatInterval = null
      }
      
      if (reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++
        const delay = Math.min(30000, reconnectAttempts * 3000)
        setTimeout(connectWebSocket, delay)
      }
    }
    
    ws.onerror = () => {
      isConnecting = false
    }
  } catch (error) {
    isConnecting = false
  }
}

function disconnectWebSocket() {
  isConnecting = false
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
  }
  if (batchTimer) {
    clearTimeout(batchTimer)
    batchTimer = null
  }
  if (ws) {
    ws.close()
    ws = null
  }
  initialChunksAccumulator = []
  pendingBatchUpdates = []
}

function startAutoStreamForMap(source = 'all') {
  stopAutoStreamForMap()
  
  if (!camerasVisible.value) return
  
  let availableCameras = []
  
  if (source === 'all') {
    availableCameras = camerasStore.cameras.filter(cam => cam && cam.url)
  } else {
    availableCameras = camerasStore.cameras.filter(cam => cam && cam.source === source && cam.url)
  }
  
  if (availableCameras.length === 0) return
  
  let currentIndex = 0
  
  const showCamera = () => {
    if (!camerasVisible.value) {
      stopAutoStreamForMap()
      return
    }
    
    if (currentIndex >= availableCameras.length) currentIndex = 0
    
    const camera = availableCameras[currentIndex]
    if (camera && camera.url && floatingStreamRef.value) {
      floatingStreamRef.value.showStream(camera)
      floatingStreamCamera.value = camera
    }
    currentIndex = (currentIndex + 1) % availableCameras.length
  }
  
  showCamera()
  
  if (availableCameras.length > 1) {
    autoStreamInterval = setInterval(showCamera, 30000)
  }
}

function stopAutoStreamForMap() {
  if (autoStreamInterval) {
    clearInterval(autoStreamInterval)
    autoStreamInterval = null
  }
}

function handleSourceFilterChange(source) {
  uiStore.setAdminSourceFilter(source)
  currentCameraSource = source
  
  if (camerasVisible.value && uiStore.activePage === 'map') {
    startAutoStreamForMap(source)
  }
}

function handleCameraSourceFromSidebar(source) {
  currentCameraSource = source
  uiStore.setAdminSourceFilter(source)
  uiStore.setSelectedCameraSource(source)
  
  if (camerasVisible.value && uiStore.activePage === 'map') {
    startAutoStreamForMap(source)
  }
}

async function refreshShips() {
  await shipsStore.fetchShips()
}

async function refreshCameras() {
  await camerasStore.fetchCameras()
}

function handleShipSelect(ship) {
  shipsStore.selectShip(ship)
  selectedShipForMap.value = ship
  selectedShipForSidebar.value = ship
}

function handleShipSelectFromSidebar(ship) {
  shipsStore.selectShip(ship)
  selectedShipForMap.value = ship
  selectedShipForSidebar.value = ship
}

function handleCameraSelectFromSidebar(camera) {
  selectedCameraForSidebar.value = camera
  camerasStore.selectCamera(camera)
  showFloatingStream(camera)
  if (camera.source) {
    uiStore.setSelectedCameraSource(camera.source)
    currentCameraSource = camera.source
  }
  stopAutoStreamForMap()
}

function handleCameraSelect(camera) {
  camerasStore.selectCamera(camera)
  showFloatingStream(camera)
  uiStore.toggleCameraPanel()
}

function showFloatingStream(camera) {
  if (camera && camera.url && floatingStreamRef.value) {
    floatingStreamCamera.value = camera
    floatingStreamRef.value.showStream(camera)
  }
}

function handleStreamClose() {
  floatingStreamCamera.value = null
  if (camerasVisible.value && uiStore.activePage === 'map') {
    startAutoStreamForMap(currentCameraSource)
  }
}

function handleMapReady(map) {
  if (camerasVisible.value) {
    setTimeout(() => startAutoStreamForMap(currentCameraSource), 500)
  }
}

function handleMapFilteredShipsChange(count) {
  visibleShipCount.value = count
}

function handleSidebarFilteredCount(count) {
  sidebarFilteredCount.value = count
}

function handleCameraSourceSelect(source) {
  uiStore.setSelectedCameraSource(source)
  currentCameraSource = source
  if (camerasVisible.value && uiStore.activePage === 'map') {
    startAutoStreamForMap(source)
  }
}

function handleToggleLayer({ layer, visible }) {
  if (layer === 'ships') {
    shipsVisible.value = visible
  } else if (layer === 'cameras') {
    camerasVisible.value = visible
    
    if (visible) {
      if (uiStore.activePage === 'map') startAutoStreamForMap(currentCameraSource)
    } else {
      stopAutoStreamForMap()
      if (floatingStreamRef.value) floatingStreamRef.value.hideStream()
      floatingStreamCamera.value = null
    }
  }
}

function openPlaybackForShip(ship) {
  trackedShip.value = ship
  playbackDate.value = ship.ts ? new Date(ship.ts) : new Date()
  playbackMode.value = true
}

function closePlayback() {
  playbackMode.value = false
  trackedShip.value = null
  playbackDate.value = null
}

function handleTimeChange(time) {}

function handleSnapshotLoad(ships) {}

function centerOnShip(ship) {
  if (ship && ship.lat && ship.lng) {
    selectedShipForMap.value = ship
    setTimeout(() => { selectedShipForMap.value = null }, 500)
  }
}

watch(() => authStore.isAuthenticated, async (isAuth, wasAuth) => {
  if (isAuth && !isLoginPage.value) {
    if (!wasAuth) {
      await reinitializeAfterLogin()
    } else {
      await Promise.all([
        shipsStore.fetchShips(),
        camerasStore.fetchCameras(),
        geofenceStore.refreshAll()
      ])
      
      if (!ws || ws.readyState !== WebSocket.OPEN) connectWebSocket()
    }
  } else if (!isAuth) {
    disconnectWebSocket()
  }
}, { immediate: true })

watch(() => route.path, (newPath) => {
  if (newPath === '/') {
    uiStore.setActivePage('map')
    if (camerasVisible.value) setTimeout(() => startAutoStreamForMap(currentCameraSource), 500)
  } else if (newPath === '/cameras') {
    uiStore.setActivePage('cameras')
    stopAutoStreamForMap()
    if (floatingStreamRef.value) floatingStreamRef.value.hideStream()
  } else if (newPath === '/stats') {
    uiStore.setActivePage('stats')
    stopAutoStreamForMap()
    if (floatingStreamRef.value) floatingStreamRef.value.hideStream()
  }
}, { immediate: true })

watch(() => uiStore.activePage, (newPage) => {
  if (newPage === 'map' && camerasVisible.value) {
    setTimeout(() => startAutoStreamForMap(currentCameraSource), 500)
  } else if (newPage !== 'map') {
    stopAutoStreamForMap()
  }
})

watch(() => uiStore.adminSourceFilter, (newSource) => {
  if (newSource !== currentCameraSource) {
    currentCameraSource = newSource
    if (uiStore.activePage === 'map' && camerasVisible.value) startAutoStreamForMap(newSource)
  }
})

watch(() => camerasStore.cameras.length, () => {
  if (camerasVisible.value && uiStore.activePage === 'map') startAutoStreamForMap(currentCameraSource)
})

function handleAuthLoginSuccess(event) {
  nextTick(() => reinitializeAfterLogin())
}

function handleAuthStateChanged(event) {
  if (event.detail.isAuthenticated) {
    nextTick(() => reinitializeAfterLogin())
  }
}

onMounted(async () => {
  uiStore.initFromStorage()
  authStore.initFromStorage()
  
  currentCameraSource = uiStore.adminSourceFilter || authStore.userSource || 'all'
  
  window.addEventListener('auth-login-success', handleAuthLoginSuccess)
  window.addEventListener('auth-state-changed', handleAuthStateChanged)
  
  if (authStore.isAuthenticated && !isLoginPage.value) {
    await reinitializeAfterLogin()
  }
})

onUnmounted(() => {
  disconnectWebSocket()
  stopAutoStreamForMap()
  shipsStore.cleanup?.()
  
  window.removeEventListener('auth-login-success', handleAuthLoginSuccess)
  window.removeEventListener('auth-state-changed', handleAuthStateChanged)
})
</script>

<style>
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}
.rotate-180 {
  transform: rotate(180deg);
}

::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}
::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}
::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>